import { ChangeEventHandler, Dispatch, SetStateAction, useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useWaUsersService from 'src/api/wa/service/WaUsersService';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import Button from 'src/ui/wards/common/button/Button';
import { userColumns } from './UserColumn';
import { FormRow } from 'src/ui/wards/common/form/FormRow';
import Input from 'src/ui/wards/common/input/Input';

const backButtonClassName = `
  w-fit whitespace-nowrap rounded-md border border-gray-400
  bg-gradient-to-b from-gray-100 to-gray-300 px-2 text-gray-800
`;

const P4_2_UserSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getUsersByNameLdapPhoneNo } = useWaUsersService();
  const [name, setName] = useState<string | undefined>(undefined);
  const [ldapId, setLdapId] = useState<string | undefined>(undefined);
  const [telephoneNumber, setTelephoneNumber] = useState<string | undefined>(undefined);
  const [hasSearched, setHasSearched] = useState(false);
  const { selectedUser, setSelectedUser, users, setUsers } = useWaUserStore();

  const createInputHandler =
    (setter: Dispatch<SetStateAction<string | undefined>>): ChangeEventHandler<HTMLInputElement> =>
    (event) => {
      setter(event.target.value === '' ? undefined : event.target.value);
    };

  const handleNameChanged = createInputHandler(setName);
  const handleLdapIdChanged = createInputHandler(setLdapId);
  const handlePhoneNoChanged = createInputHandler(setTelephoneNumber);

  const handleSearch = async () => {
    setHasSearched(true);
    if (name || ldapId || telephoneNumber) {
      getUsersByNameLdapPhoneNo(name, ldapId, telephoneNumber);
    } else {
      setUsers([]);
    }
  };

  const us = [...users]
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }))
    .slice(0, 50);

  useEffect(() => {
    setUsers([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedUser) {
      setLdapId(selectedUser.ldapId);
    }
  }, [selectedUser]);

  return (
    <div className="h-full space-y-2">
      <div className="space-y-1 text-2xl font-bold">{t('wards.desktop.userSearch.title')}</div>
      <div className="flex justify-end">
        <Button
          title={t('wards.common.back')}
          onClick={() => navigate(-1)}
          className={backButtonClassName}
        />
      </div>
      <div className="space-y-4 p-4">
        <FormRow title={t('wards.desktop.userSearch.userName')}>
          <Input onChange={handleNameChanged} value={name || ''} />
        </FormRow>
        <FormRow title={t('wards.desktop.userSearch.ldapId')}>
          <Input onChange={handleLdapIdChanged} value={ldapId || ''} />
        </FormRow>
        <FormRow title={t('wards.desktop.userSearch.phoneNo')}>
          <Input onChange={handlePhoneNoChanged} value={telephoneNumber || ''} />
        </FormRow>
        <div className="flex justify-end pt-2">
          <Button
            title={t('wards.desktop.unaddressedPropertiesMap.search')}
            className="w-fit whitespace-nowrap rounded-md border border-gray-400
                         bg-gradient-to-b from-gray-100 to-gray-300 p-1 text-gray-800 
                         disabled:cursor-not-allowed disabled:border-gray-300 disabled:from-gray-200 
                         disabled:to-gray-200 disabled:text-gray-400 disabled:shadow-none"
            onClick={handleSearch}
          />
        </div>
      </div>

      {hasSearched && !name && !ldapId && !telephoneNumber && (
        <p className="text-center text-sm text-red-600">
          {t('wards.desktop.userSearch.errorMessage')}
        </p>
      )}

      {hasSearched && (
        <div>
          <DataTable
            data={us}
            noDataComponent={t('wards.common.noResults')}
            columns={userColumns(navigate, t, setSelectedUser)}
          />
        </div>
      )}

      <div className="flex justify-end">
        <Button
          title={t('wards.common.back')}
          onClick={() => navigate(-1)}
          className={backButtonClassName}
        />
      </div>
    </div>
  );
};

export default P4_2_UserSearch;
